import { useState, useEffect, useRef } from "react";
import { supabase } from "./db";
import AdminDashboard from "./AdminDashboard";
import AttributeManager from "./AttributeManager";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SupplierLogs from "./SupplierLogs";

// 👇 FIXNÉ STĹPCE A MAPOVANIE Z ADMIN DASHBOARDU
const FIXED_COLUMNS = ["akcie", "status", "content_status", "internal_sku"];

const COLUMN_NAMES_MAP = {
  ean: "EAN Kód",
  company_name: "Dodávateľ",
  product_name: "Názov produktu",
  category: "Kategória na webe",
  price: "Cena výrobcu (€)",
  stock: "Sklad (ks)",
  brand: "Brand",
  created_at: "Dátum pridania",
  approved_at: "Čas rozhodnutia",
  weight_brutto_g: "Váha Brutto (g)",
  weight_netto_g: "Váha Netto (g)",
  short_description: "Krátky text",
  description: "Hlavný popis",
  image_url: "Obrázok",
  product_line: "Produktová rada",
  sukl: "ŠÚKL",
  adc: "ADC kód",
  predecessor: "Predchodca",
  dr_max_code: "Kód Dr.Max",
  base_mj: "Základná MJ",
  ratio_to_zmj: "Pomer k ZMJ",
  packaging: "Balenie",
  ingredients: "Zloženie",
  dosage: "Dávkovanie",
  nutritional_values: "Výživové hodnoty",
  width_mm: "Šírka (mm)",
  height_mm: "Výška (mm)",
  depth_mm: "Hĺbka (mm)",
  pieces_in_carton: "Ks v kartóne",
  pieces_on_pallet: "Ks na palete",
  manufacturer: "Výrobca",
  country_of_origin: "Krajina pôvodu",
  customs_code: "Colný sadzobník",
  recycling_fee: "Recyklačný poplatok",
  vat_percent: "% DPH",
  dmoc: "DMOC (€)",
  returnable_packaging: "Zálohovaný obal",
  total_weight: "Celková hmotnosť",
  listing_date: "Zalistovanie od",
  listing_type: "Typ listingu",
  admin_fee: "Admin poplatok",
  turnover_reward_percent: "Odplata z obratu (%)",
  tracked_transaction: "Sledovaná transakcia",
  tracked_price: "Sledovaná cena",
};

// 👑 TÍM ADMINISTRÁTOROV
const ADMIN_LIST = [
  {
    name: "Adrian Tran",
    email: "adriantran.tt@gmail.com",
    role: "Obchodný manažér",
  },
  { name: "Admin 1", email: "admin1@1.sk", role: "Obchodný manažér" },
];

export default function MasterDashboard({ session }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCatalog, setShowCatalog] = useState(false);

  const [logFilters, setLogFilters] = useState({
    action: "",
    product_name: "",
  });
  const [logSort, setLogSort] = useState({
    key: "created_at",
    direction: "desc",
  });

  const [globalStats, setGlobalStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    suppliers: 0,
    skuAssigned: 0,
    webListed: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // 👇 STAVY PRE DYNAMICKÉ STĹPCE (SKOPÍROVANÉ Z ADMIN DASHBOARD)
  const [dbColumns, setDbColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([
    "image_url",
    "company_name",
    "product_name",
    "category",
    "ean",
    "price",
    "stock",
    "created_at",
  ]);
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const columnChooserRef = useRef(null);

  useEffect(() => {
    if (activeTab === "overview") {
      fetchGlobalStats();
      fetchColumnsFromDB();
    }
  }, [activeTab]);

  // Kliknutie mimo Column Choosera ho zatvorí
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnChooserRef.current &&
        !columnChooserRef.current.contains(event.target)
      ) {
        setShowColumnChooser(false);
      }
    };
    if (showColumnChooser)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnChooser]);

  const fetchColumnsFromDB = async () => {
    const { data, error } = await supabase.rpc("get_products_columns");
    if (!error && data && data.length > 0) {
      const exclude = [
        "id",
        "supplier_id",
        "locked_by_master",
        "approved_at",
        "status",
        "content_status",
        "internal_sku",
        "updated_at",
      ];
      let cleanCols = data.filter((c) => !exclude.includes(c));
      if (!cleanCols.includes("company_name"))
        cleanCols.unshift("company_name");
      cleanCols.sort((a, b) =>
        (COLUMN_NAMES_MAP[a] || a).localeCompare(COLUMN_NAMES_MAP[b] || b)
      );
      setDbColumns(cleanCols);
    }
  };

  const fetchGlobalStats = async () => {
    setLoadingStats(true);
    try {
      const { data: products } = await supabase
        .from("products")
        .select("status, internal_sku, content_status");
      const { count: suppliersCount } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });

      if (products) {
        const total = products.length;
        const approved = products.filter(
          (p) => p.status === "Schválené"
        ).length;
        const rejected = products.filter(
          (p) => p.status === "Neschválené"
        ).length;
        const pending = total - approved - rejected;
        const skuAssigned = products.filter(
          (p) => p.internal_sku && String(p.internal_sku).trim() !== ""
        ).length;
        const webListed = products.filter(
          (p) => p.content_status === "Zalistované na webe"
        ).length;
        setGlobalStats({
          total,
          approved,
          rejected,
          pending,
          suppliers: suppliersCount || 0,
          skuAssigned,
          webListed,
        });
      }
    } catch (err) {
      console.error("Chyba:", err);
    }
    setLoadingStats(false);
  };

  const handleSelectAdmin = async (admin) => {
    setSelectedAdmin(admin);
    setLoadingLogs(true);
    setSearchTerm("");
    setLogFilters({ action: "", product_name: "" });

    const { data, error } = await supabase
      .from("activity_logs")
      .select("*")
      .ilike("user_email", admin.email);
    if (!error) setLogs(data || []);
    setLoadingLogs(false);
  };

  const handleExportLogs = () => {
    const excelData = finalLogs.map((l) => ({
      "Dátum a Čas": new Date(l.created_at).toLocaleString("sk-SK"),
      "Typ Úkonu": l.action,
      Produkt: l.product_name,
      Detaily: JSON.stringify(l.details || {}),
    }));
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Ulohy_${selectedAdmin.name}`);
    XLSX.writeFile(wb, `Report_Uloh_${selectedAdmin.name}.xlsx`);
  };

  const renderActionBadge = (action) => {
    if (action === "STATUS_CHANGE")
      return <span style={badgeBlue}>Zmena statusu</span>;
    if (action === "UPDATE")
      return <span style={badgeWarning}>Úprava dát</span>;
    if (action === "DELETE")
      return <span style={badgeDanger}>Zmazanie produktu</span>;
    return <span style={badgeGray}>{action || "Neznáme"}</span>;
  };

  const requestLogSort = (key) => {
    let direction = "asc";
    if (logSort.key === key && logSort.direction === "asc") direction = "desc";
    setLogSort({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (logSort.key !== columnName) return " ↕️";
    return logSort.direction === "asc" ? " 🔼" : " 🔽";
  };

  let finalLogs = logs.filter((l) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesGlobal =
      (l.product_name || "").toLowerCase().includes(searchLower) ||
      (l.action || "").toLowerCase().includes(searchLower);
    const matchesAction = (l.action || "")
      .toLowerCase()
      .includes(logFilters.action.toLowerCase());
    const matchesProduct = (l.product_name || "")
      .toLowerCase()
      .includes(logFilters.product_name.toLowerCase());
    return matchesGlobal && matchesAction && matchesProduct;
  });

  finalLogs.sort((a, b) => {
    if (!logSort.key) return 0;
    let valA = a[logSort.key],
      valB = b[logSort.key];
    if (logSort.key === "created_at") {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    } else {
      valA = String(valA || "").toLowerCase();
      valB = String(valB || "").toLowerCase();
    }
    if (valA < valB) return logSort.direction === "asc" ? -1 : 1;
    if (valA > valB) return logSort.direction === "asc" ? 1 : -1;
    return 0;
  });

  const chartData = [
    { name: "Schválené", pocet: globalStats.approved, color: "#10b981" },
    { name: "Čakačka", pocet: globalStats.pending, color: "#f59e0b" },
    { name: "Neschválené", pocet: globalStats.rejected, color: "#ef4444" },
  ];

  if (showCatalog) {
    return (
      <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
        <div
          style={{ maxWidth: "100%", padding: "0 20px", marginBottom: "15px" }}
        >
          <button
            onClick={() => {
              setShowCatalog(false);
              fetchGlobalStats();
            }}
            className="btn-outline"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "white",
              padding: "10px 20px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              cursor: "pointer",
            }}
          >
            <span>←</span> Späť na Master Dashboard
          </button>
        </div>
        {/* Tu vykreslíme ten plnohodnotný zložitejší AdminDashboard komponent so stránkovaním */}
        <AdminDashboard session={session} />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "0 20px",
        maxWidth: "1200px",
        margin: "0 auto",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {!selectedAdmin && !showCatalog && (
        <div
          style={{
            display: "flex",
            gap: "20px",
            marginBottom: "30px",
            borderBottom: "2px solid rgba(226, 232, 240, 0.6)",
            paddingBottom: "10px",
          }}
        >
          <button
            onClick={() => setActiveTab("overview")}
            style={{
              ...tabBtnStyle,
              color: activeTab === "overview" ? "#3b82f6" : "#64748b",
              borderBottom:
                activeTab === "overview"
                  ? "3px solid #3b82f6"
                  : "3px solid transparent",
            }}
          >
            📊 Prehľad a Logy
          </button>
          <button
            onClick={() => setActiveTab("filters")}
            style={{
              ...tabBtnStyle,
              color: activeTab === "filters" ? "#8b5cf6" : "#64748b",
              borderBottom:
                activeTab === "filters"
                  ? "3px solid #8b5cf6"
                  : "3px solid transparent",
            }}
          >
            ⚙️ Nastavenie kategórií a filtrov
          </button>
        </div>
      )}

      {activeTab === "filters" && !selectedAdmin && !showCatalog && (
        <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
          <AttributeManager />
        </div>
      )}

      {activeTab === "overview" && (
        <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
          {!selectedAdmin ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2
                  style={{
                    color: "#0f172a",
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: "800",
                    letterSpacing: "-0.5px",
                  }}
                >
                  📊 Prehľad listingu noviniek
                </h2>
                <button
                  onClick={() => setShowCatalog(true)}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#0f172a",
                    color: "white",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <span>📦</span> Pozrieť všetky Produkty
                </button>
              </div>

              {loadingStats ? (
                <p style={{ color: "#64748b", fontSize: "14px" }}>
                  Načítavam dáta štatistík...
                </p>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "20px",
                    marginBottom: "30px",
                    alignItems: "stretch",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{ ...statCardStyle, borderLeftColor: "#3b82f6" }}
                    >
                      <div style={statTitleStyle}>Všetky produkty</div>
                      <div style={statValueStyle}>{globalStats.total}</div>
                    </div>
                    <div
                      style={{ ...statCardStyle, borderLeftColor: "#8b5cf6" }}
                    >
                      <div style={statTitleStyle}>Dodávatelia</div>
                      <div style={{ ...statValueStyle, color: "#6d28d9" }}>
                        {globalStats.suppliers}
                      </div>
                    </div>
                    <div
                      style={{ ...statCardStyle, borderLeftColor: "#f59e0b" }}
                    >
                      <div style={statTitleStyle}>V čakačke</div>
                      <div style={{ ...statValueStyle, color: "#d97706" }}>
                        {globalStats.pending}
                      </div>
                    </div>
                    <div
                      style={{ ...statCardStyle, borderLeftColor: "#10b981" }}
                    >
                      <div style={statTitleStyle}>Schválené</div>
                      <div style={{ ...statValueStyle, color: "#059669" }}>
                        {globalStats.approved}
                      </div>
                    </div>
                    <div
                      style={{ ...statCardStyle, borderLeftColor: "#facc15" }}
                    >
                      <div style={statTitleStyle}>Priradené SKU</div>
                      <div style={{ ...statValueStyle, color: "#ca8a04" }}>
                        {globalStats.skuAssigned}
                      </div>
                    </div>
                    <div
                      style={{ ...statCardStyle, borderLeftColor: "#14b8a6" }}
                    >
                      <div style={statTitleStyle}>Na webe</div>
                      <div style={{ ...statValueStyle, color: "#0d9488" }}>
                        {globalStats.webListed}
                      </div>
                    </div>
                    <div
                      style={{
                        ...statCardStyle,
                        borderLeftColor: "#ef4444",
                        gridColumn: "1 / -1",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={statTitleStyle}>Neschválené</div>
                      <div style={{ ...statValueStyle, color: "#dc2626" }}>
                        {globalStats.rejected}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                      padding: "20px",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "16px",
                      height: "100%",
                      minHeight: "220px",
                      boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 15px 0",
                        color: "#334155",
                        fontSize: "13px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Graf aktuálnych stavov
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          margin={{ top: 5, right: 10, left: -25, bottom: -5 }}
                          barSize={35}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: "#64748b",
                              fontSize: 11,
                              fontWeight: "600",
                            }}
                            dy={10}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#64748b", fontSize: 11 }}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(241, 245, 249, 0.5)" }}
                            contentStyle={{
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
                              fontSize: "12px",
                              padding: "8px 12px",
                            }}
                          />
                          <Bar dataKey="pocet" radius={[6, 6, 0, 0]}>
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              <SupplierLogs />

              <h2
                style={{
                  color: "#0f172a",
                  marginBottom: "20px",
                  fontSize: "22px",
                  fontWeight: "800",
                  borderTop: "1px solid rgba(226, 232, 240, 0.8)",
                  paddingTop: "25px",
                  letterSpacing: "-0.5px",
                }}
              >
                👥 Zoznam obchodných manažérov
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "20px",
                  paddingBottom: "40px",
                }}
              >
                {ADMIN_LIST.map((admin, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectAdmin(admin)}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      padding: "25px",
                      transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                      borderRadius: "16px",
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.02)",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 15px 30px -5px rgba(0, 0, 0, 0.05)";
                      e.currentTarget.style.borderColor = "#93c5fd";
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 10px rgba(0,0,0,0.02)";
                      e.currentTarget.style.borderColor =
                        "rgba(226, 232, 240, 0.8)";
                      e.currentTarget.style.backgroundColor =
                        "rgba(255, 255, 255, 0.7)";
                    }}
                  >
                    <div
                      style={{
                        width: "65px",
                        height: "65px",
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
                        color: "white",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "24px",
                        fontWeight: "bold",
                        marginBottom: "15px",
                        boxShadow: "0 4px 15px rgba(59, 130, 246, 0.3)",
                      }}
                    >
                      {admin.name.charAt(0).toUpperCase()}
                    </div>
                    <h3
                      style={{
                        margin: "0 0 5px 0",
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#0f172a",
                      }}
                    >
                      {admin.name}
                    </h3>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "15px",
                      }}
                    >
                      {admin.email}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        background: "#eff6ff",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        color: "#2563eb",
                        fontWeight: "700",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      {admin.role}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => setSelectedAdmin(null)}
                style={{
                  marginBottom: "25px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "white",
                  border: "1px solid #cbd5e1",
                  padding: "10px 16px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#475569",
                  cursor: "pointer",
                }}
              >
                <span>←</span> Späť na hlavný panel
              </button>

              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "25px",
                  padding: "25px",
                  borderRadius: "20px",
                  boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "15px" }}
                >
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
                      color: "white",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "22px",
                      fontWeight: "bold",
                      boxShadow: "0 4px 10px rgba(59, 130, 246, 0.3)",
                    }}
                  >
                    {selectedAdmin.name.charAt(0)}
                  </div>
                  <div>
                    <h2
                      style={{
                        margin: "0 0 5px 0",
                        color: "#0f172a",
                        fontSize: "22px",
                        fontWeight: "800",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      Úlohy: {selectedAdmin.name}
                    </h2>
                    <p
                      style={{
                        margin: 0,
                        color: "#64748b",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {selectedAdmin.email}{" "}
                      <span style={{ color: "#cbd5e1", margin: "0 8px" }}>
                        |
                      </span>{" "}
                      Počet úkonov:{" "}
                      <span style={{ color: "#3b82f6", fontWeight: "700" }}>
                        {finalLogs.length}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleExportLogs}
                  disabled={finalLogs.length === 0}
                  style={{
                    padding: "12px 20px",
                    fontSize: "14px",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                    boxShadow: "0 4px 10px rgba(16, 185, 129, 0.3)",
                    opacity: finalLogs.length === 0 ? 0.5 : 1,
                  }}
                >
                  💾 Export do Excelu
                </button>
              </div>

              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #e2e8f0",
                  padding: "20px",
                  marginBottom: "25px",
                  borderRadius: "16px",
                }}
              >
                <input
                  type="text"
                  placeholder="🔍 Globálne vyhľadávanie v logoch..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    maxWidth: "450px",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    outline: "none",
                    backgroundColor: "white",
                  }}
                />
              </div>

              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "20px",
                  border: "1px solid #e2e8f0",
                  padding: "0",
                  overflowX: "auto",
                  boxShadow: "0 10px 30px -10px rgba(0,0,0,0.03)",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: "900px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f8fafc",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      <th
                        style={{ ...thStyle, cursor: "pointer" }}
                        onClick={() => requestLogSort("created_at")}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          Čas a Dátum {getSortIcon("created_at")}
                        </div>
                      </th>
                      <th style={thStyle}>
                        <div
                          style={{
                            cursor: "pointer",
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          onClick={() => requestLogSort("action")}
                        >
                          Vykonaná akcia {getSortIcon("action")}
                        </div>
                        <input
                          type="text"
                          placeholder="Filtrovať..."
                          style={filterInputStyle}
                          value={logFilters.action}
                          onChange={(e) =>
                            setLogFilters({
                              ...logFilters,
                              action: e.target.value,
                            })
                          }
                        />
                      </th>
                      <th style={thStyle}>
                        <div
                          style={{
                            cursor: "pointer",
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          onClick={() => requestLogSort("product_name")}
                        >
                          Názov produktu {getSortIcon("product_name")}
                        </div>
                        <input
                          type="text"
                          placeholder="Filtrovať..."
                          style={filterInputStyle}
                          value={logFilters.product_name}
                          onChange={(e) =>
                            setLogFilters({
                              ...logFilters,
                              product_name: e.target.value,
                            })
                          }
                        />
                      </th>
                      <th style={{ ...thStyle, width: "30%" }}>Detail úkonu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalLogs.map((log) => (
                      <tr
                        key={log.id}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background-color 0.2s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8fafc")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <td style={tdStyle}>
                          <strong style={{ color: "#334155" }}>
                            {new Date(log.created_at).toLocaleString("sk-SK")}
                          </strong>
                        </td>
                        <td style={tdStyle}>{renderActionBadge(log.action)}</td>
                        <td style={tdStyle}>
                          <strong style={{ color: "#0f172a" }}>
                            {log.product_name}
                          </strong>
                        </td>
                        <td style={tdStyle}>
                          {log.action === "STATUS_CHANGE" ? (
                            <span
                              style={{
                                color:
                                  log.details?.newStatus === "Schválené"
                                    ? "#059669"
                                    : "#dc2626",
                                fontWeight: "700",
                                background:
                                  log.details?.newStatus === "Schválené"
                                    ? "#ecfdf5"
                                    : "#fef2f2",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                fontSize: "12px",
                              }}
                            >
                              Nastavené na:{" "}
                              {log.details?.newStatus || "Neznámy status"}
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#475569",
                                background: "#f1f5f9",
                                padding: "4px 10px",
                                borderRadius: "6px",
                                fontWeight: "500",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              {log.action === "DELETE"
                                ? "🗑️ Produkt bol vymazaný"
                                : "✏️ Dáta boli upravené"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {finalLogs.length === 0 && (
                  <div
                    style={{
                      padding: "60px",
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "15px",
                    }}
                  >
                    Žiadne záznamy nezodpovedajú filtrom.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// 🔥 ŠTÝLY
const tabBtnStyle = {
  background: "transparent",
  border: "none",
  padding: "10px 10px",
  fontSize: "16px",
  fontWeight: "700",
  cursor: "pointer",
  transition: "all 0.2s ease",
};
const statCardStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  backdropFilter: "blur(10px)",
  padding: "12px 16px",
  borderLeft: "3px solid",
  borderRadius: "12px",
  borderTop: "1px solid rgba(226, 232, 240, 0.6)",
  borderRight: "1px solid rgba(226, 232, 240, 0.6)",
  borderBottom: "1px solid rgba(226, 232, 240, 0.6)",
  boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
};
const statTitleStyle = {
  color: "#64748b",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};
const statValueStyle = {
  fontSize: "24px",
  fontWeight: "800",
  color: "#1e293b",
  marginTop: "4px",
};
const thStyle = {
  padding: "16px 20px",
  color: "#475569",
  fontSize: "13px",
  textAlign: "left",
  verticalAlign: "top",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};
const tdStyle = {
  padding: "16px 20px",
  fontSize: "14px",
  verticalAlign: "middle",
  color: "#475569",
};
const filterInputStyle = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  fontSize: "12px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  outline: "none",
  marginTop: "5px",
  backgroundColor: "white",
};
const badgeBlue = {
  background: "#eff6ff",
  color: "#2563eb",
  padding: "6px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "700",
  border: "1px solid #bfdbfe",
};
const badgeWarning = {
  background: "#fffbeb",
  color: "#d97706",
  padding: "6px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "700",
  border: "1px solid #fde68a",
};
const badgeDanger = {
  background: "#fef2f2",
  color: "#dc2626",
  padding: "6px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "700",
  border: "1px solid #fecaca",
};
const badgeGray = {
  background: "#f8fafc",
  color: "#64748b",
  padding: "6px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "700",
  border: "1px solid #e2e8f0",
};
